import { useEffect, useMemo } from 'react';

import {
  Box,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel
} from '@material-ui/core';

import { clsx } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';

import { projectPurposeOptions } from './types';
import useStyles from './useStyles';
import OutlinedTextField, {
  CurrencyFormatInput
} from '@routes/Common/OutlinedTextField';

import {
  useFormContext as useFormContextFromContainer,
  Controller
} from 'react-hook-form';

import { MuiDatePicker } from '@routes/RequestForQuotation/components/Common/RequestInfoInputs';

import { getMinDateFromToday } from '@utils/date';
import { IRequestState } from '@modules/request';

import { debounce } from 'lodash';

import {
  CapaActivityType,
  CapaQuotationProductPurpose,
  CapaQuotationServiceType
} from '@generated/graphql';
import { numberToKorean } from '@utils/formatter';

import useUserActionLog, {
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';
import { useSelector } from 'react-redux';
import { RootState } from '@modules/index';

interface Props {
  matches: boolean;
  serviceType: CapaQuotationServiceType;
  maxEstimatedBudget?: number;
  handleUpdateRfqInfo: (payload: Partial<IRequestState>) => void;
}

interface ChildProps {
  onKeydown: () => void;
  handleUpdateRfqInfo: (payload: Partial<IRequestState>) => void;
  matches: boolean;
  maxEstimatedBudget?: number;
  serviceType: CapaQuotationServiceType;
}

const RequestForQuotationProjectInfo = ({
  matches,
  serviceType,
  maxEstimatedBudget = 0,
  handleUpdateRfqInfo
}: Props) => {
  const classes = useStyles();

  const { getValues, errors } = useFormContextFromContainer();

  const fieldValues = () => {
    const projectName = getValues('projectName');
    const maxBudget = getValues('maxEstimatedBudget');
    const projectPurposeDetail = getValues('projectPurposeDetail');

    handleUpdateRfqInfo({
      projectName: projectName,
      maxEstimatedBudget: maxBudget,
      projectPurposeDetail: projectPurposeDetail
    });
  };

  const onKeydown = debounce(fieldValues, 350);

  useEffect(() => {
    if (errors) {
      const errorsToArray = Object.keys(errors);
      const infoErrors = errorsToArray.some(
        (key) =>
          key === 'dueDate' ||
          key === 'projectName' ||
          key === 'projectPurpose' ||
          key === 'maxEstimatedBudget'
      );
      if (!errorsToArray.includes('uploadedDrawingFiles') && infoErrors) {
        document.getElementById('projectinfo')?.scrollIntoView();
      }
    }
  }, [errors]);

  return (
    <Box
      id="projectinfo"
      component="section"
      className={clsx(classes['rfq-project-info'], classes['rfq-info-section'])}
    >
      <CustomizedTypography
        className="title"
        align="center"
        color="textPrimary"
        gutterBottoms={40}
      >
        프로젝트 정보를 입력해주세요 *
      </CustomizedTypography>

      {/* 프로젝트명 */}
      <ProjectNameComponent
        matches={matches}
        serviceType={serviceType}
        onKeydown={onKeydown}
        handleUpdateRfqInfo={handleUpdateRfqInfo}
      />

      {/* 제품 용도 */}
      <ProjectPurposeComponent
        matches={matches}
        serviceType={serviceType}
        onKeydown={onKeydown}
        handleUpdateRfqInfo={handleUpdateRfqInfo}
      />

      {/* 납기일 */}
      <DueDateComponent
        matches={matches}
        serviceType={serviceType}
        onKeydown={onKeydown}
        handleUpdateRfqInfo={handleUpdateRfqInfo}
      />

      {/* 추정 예산 범위 */}
      <ExpectedBudgetComponent
        matches={matches}
        serviceType={serviceType}
        onKeydown={onKeydown}
        maxEstimatedBudget={maxEstimatedBudget}
        handleUpdateRfqInfo={handleUpdateRfqInfo}
      />
    </Box>
  );
};

export default RequestForQuotationProjectInfo;

const ProjectNameComponent = ({ serviceType, onKeydown }: ChildProps) => {
  const { control, errors } = useFormContextFromContainer();

  const { onInsertUserActionLogOne } = useUserActionLog();

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_ProjectTitle;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_ProjectTitle;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_ProjectTitle;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_ProjectTitle;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_ProjectTitle;
    }
  }, [serviceType]);

  const onLog = () => {
    if (logId) {
      return onInsertUserActionLogOne(logId, CapaActivityType.ButtonClick);
    }
  };

  return (
    <div className="info-field gutter-bottom">
      <CustomizedTypography
        variant="body1"
        color="textPrimary"
        fontWeight="500"
        gutterBottoms={4}
      >
        프로젝트명 *
      </CustomizedTypography>
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        gutterBottoms={12}
      >
        한 문장으로 프로젝트를 설명할 수 있도록 작성해주세요 (최대 20자)
      </CustomizedTypography>

      <Controller
        control={control}
        name="projectName"
        defaultValue=""
        rules={{ required: true, maxLength: 20 }}
        render={(render) => (
          <OutlinedTextField
            {...render}
            onChange={(e) => {
              render.onChange(e.target.value.trimStart());
            }}
            align="left"
            fullWidth
            error={errors['projectName'] ? true : false}
            placeholder="예) 청소용 가전제품 내부 부품 가공"
            size="large"
            onClick={onLog}
            onKeyDown={() => onKeydown()}
            onError={(e) => {
              console.log(e);
            }}
          />
        )}
      />

      {errors['projectName']?.type === 'required' && (
        <span className="errorMessage">프로젝트명을 입력해주세요.</span>
      )}

      {errors['projectName']?.type === 'maxLength' && (
        <span className="errorMessage">20자 이하로 입력해주세요.</span>
      )}
    </div>
  );
};

const ProjectPurposeComponent = ({
  serviceType,
  handleUpdateRfqInfo,
  onKeydown
}: ChildProps) => {
  const classes = useStyles();
  const { control, errors, watch, setValue } = useFormContextFromContainer();
  const projectPurpose = watch('projectPurpose');

  const { onInsertUserActionLogOne } = useUserActionLog();

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_ProjectPurpose;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_ProjectPurpose;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_ProjectPurpose;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_ProjectPurpose;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_ProjectPurpose;
    }
  }, [serviceType]);

  const onLog = () => {
    if (logId) {
      return onInsertUserActionLogOne(logId, CapaActivityType.ButtonClick);
    }
  };

  return (
    <div className="info-field gutter-bottom">
      <CustomizedTypography
        variant="body1"
        color="textPrimary"
        fontWeight="500"
        gutterBottoms={4}
      >
        제품 용도 *
      </CustomizedTypography>
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        gutterBottoms={12}
      >
        제작하시는 제품의 용도를 선택해주세요.
      </CustomizedTypography>

      <Grid container className="project-purpose">
        <Grid item sm={6} xs={12} className="purpose-select-input">
          <Controller
            defaultValue={''}
            control={control}
            name="projectPurpose"
            rules={{ required: true }}
            error={errors['projectPurpose'] ? true : false}
            render={(render) => (
              <Select
                {...render}
                fullWidth
                classes={{
                  select: clsx(classes['option-select'])
                }}
                variant="outlined"
                displayEmpty
                error={errors['projectPurpose'] ? true : false}
                onClick={onLog}
                onChange={(e) => {
                  handleUpdateRfqInfo({
                    projectPurpose: e.target
                      .value as CapaQuotationProductPurpose
                  });

                  setValue('projectPurposeDetail', '');

                  return render.onChange(e);
                }}
              >
                <MenuItem value="" disabled>
                  제품 용도
                </MenuItem>
                {Object.entries(projectPurposeOptions).map((option, index) => {
                  const enumValue = option[0];
                  const label = option[1];
                  if (['part1', 'part2', 'part3'].includes(enumValue)) {
                    return (
                      <MenuItem key={index} disabled>
                        {label}
                      </MenuItem>
                    );
                  }
                  return (
                    <MenuItem
                      key={index}
                      value={enumValue}
                      classes={{ root: 'option-menu-item' }}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            )}
          />
          {errors['projectPurpose'] && (
            <span className="errorMessage">제품 용도를 선택해주세요.</span>
          )}
        </Grid>

        {projectPurpose && (
          <Grid item sm={6} xs={12}>
            <Controller
              control={control}
              name="projectPurposeDetail"
              defaultValue=""
              render={(render) => (
                <OutlinedTextField
                  {...render}
                  align="left"
                  fullWidth
                  placeholder="상세 제품 용도를 적어주세요 (선택)"
                  size="large"
                  onKeyDown={() => onKeydown()}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

const DueDateComponent = ({ serviceType, handleUpdateRfqInfo }: ChildProps) => {
  const { control, errors } = useFormContextFromContainer();

  const { onInsertUserActionLogOne } = useUserActionLog();

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_DueDate;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_DueDate;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_DueDate;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_DueDate;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_DueDate;
    }
  }, [serviceType]);

  const onLog = () => {
    if (logId) {
      return onInsertUserActionLogOne(logId, CapaActivityType.ButtonClick);
    }
  };

  return (
    <div className="info-field gutter-bottom">
      <CustomizedTypography
        variant="body1"
        color="textPrimary"
        fontWeight="500"
        gutterBottoms={4}
      >
        납기일 *
      </CustomizedTypography>
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        gutterBottoms={12}
      >
        가공 업체의 상황에 따라 가공 가능한 납기일이 다를 수 있습니다. 납기일
        협의가 가능한 경우, 더 많은 견적을 받아 비교할 수 있습니다.
      </CustomizedTypography>

      <Controller
        name="dueDate"
        control={control}
        defaultValue=""
        rules={{ required: true }}
        render={(render) => {
          const minDate = getMinDateFromToday(7);

          return (
            <MuiDatePicker
              minDate={minDate}
              name={render.name}
              value={render.value && new Date(render.value)}
              onChange={(e) => {
                render.onChange(e);
                handleUpdateRfqInfo({ dueDate: e });
              }}
              errors={errors}
              sendLog={onLog}
              inputProps={{
                name: render.name
              }}
            />
          );
        }}
      />
      {errors['dueDate'] && (
        <span className="errorMessage">납기일을 입력해주세요.</span>
      )}

      <Controller
        control={control}
        name="dueNegotiable"
        defaultValue={''}
        render={(render) => {
          const checkedrender = {
            ...render,
            value: typeof render.value === 'string' ? false : render.value
          };
          return (
            <FormControlLabel
              className="sub-checkbox"
              label="납기일 협의 가능"
              control={
                <Checkbox
                  {...checkedrender}
                  checked={checkedrender.value}
                  onChange={(e) => {
                    render.onChange(e.target.checked);
                    handleUpdateRfqInfo({ dueNegotiable: e.target.checked });
                  }}
                  color="primary"
                />
              }
            />
          );
        }}
      />
    </div>
  );
};

const ExpectedBudgetComponent = ({
  onKeydown,
  serviceType,
  maxEstimatedBudget = 0,
  handleUpdateRfqInfo
}: ChildProps) => {
  const budgetOpenDefaultValue = useSelector<RootState, boolean>(
    (state) => state.request.budgetOpen
  );

  const { control, errors } = useFormContextFromContainer();

  const { onInsertUserActionLogOne } = useUserActionLog();

  const logIdMax = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_EstimatedBudget_MAX;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_EstimatedBudget_MAX;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_EstimatedBudget_MAX;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_EstimatedBudget_MAX;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_EstimatedBudget_MAX;
    }
  }, [serviceType]);

  const onLogMax = () => {
    if (logIdMax) {
      return onInsertUserActionLogOne(logIdMax, CapaActivityType.ButtonClick);
    }
  };

  return (
    <div className="info-field gutter-bottom">
      <CustomizedTypography
        variant="body1"
        color="textPrimary"
        fontWeight="500"
        gutterBottoms={4}
      >
        추정 예산 *
      </CustomizedTypography>
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        gutterBottoms={12}
      >
        추정 예산 범위를 작성해주세요. 예산 범위에 맞는 파트너를 매칭해드립니다.
        최대 천억 단위(숫자 12자리)까지 입력 가능합니다.
      </CustomizedTypography>

      <Controller
        control={control}
        name="maxEstimatedBudget"
        defaultValue=""
        rules={{
          required: true,
          validate: {
            required: (value) => {
              return value ? true : false;
            },
            zero: (value) => {
              return Number(value) !== 0 ? true : false;
            },
            range: (value) => {
              return value.toString().length < 13 ? true : false;
            }
          }
        }}
        render={(render) => (
          <Box width="100%" position="relative" maxWidth={450}>
            <OutlinedTextField
              {...render}
              size="large"
              className="budget-input"
              fullWidth
              align="left"
              placeholder="숫자만 입력"
              onClick={onLogMax}
              error={errors['maxEstimatedBudget'] ? true : false}
              InputProps={{
                inputComponent: CurrencyFormatInput,
                endAdornment: (
                  <CustomizedTypography variant="subtitle2" color="textPrimary">
                    원
                  </CustomizedTypography>
                )
              }}
              onKeyDown={() => onKeydown()}
              onChange={(e) => {
                if (e.target.value?.length > 12) {
                  return;
                }

                render.onChange(e.target.value as any);
              }}
            />
            {!errors['maxEstimatedBudget'] && (
              <span className="budget cost">
                {maxEstimatedBudget
                  ? numberToKorean(maxEstimatedBudget) + '원'
                  : ''}
              </span>
            )}

            {errors['maxEstimatedBudget']?.type === 'zero' && (
              <span className="errorMessage budget">
                0 보다 큰 수를 입력해주세요.
              </span>
            )}
            {errors['maxEstimatedBudget']?.type === 'required' && (
              <span className="errorMessage budget">
                추정예산 범위를 입력해주세요.
              </span>
            )}
          </Box>
        )}
      />

      <Controller
        name="budgetOpen"
        defaultValue={budgetOpenDefaultValue ?? false}
        control={control}
        render={(renderProps) => (
          <FormControlLabel
            className="sub-checkbox"
            label="추정예산을 파트너에게 공개하지 않습니다."
            control={
              <Checkbox
                {...renderProps}
                checked={!renderProps.value}
                onChange={(e) => {
                  handleUpdateRfqInfo({ budgetOpen: !e.target.checked });
                  renderProps.onChange(!e.target.checked);
                }}
                color="primary"
              />
            }
          />
        )}
      />
    </div>
  );
};
